import { IssueCostCondition, IssueCostRuleForm, IssueCostRuleUpsert } from './types';

export const EMPTY_RULE_FORM: IssueCostRuleForm = {
	providerId: '',
	validatingCarrier: '',
	tariffType: '',
	commissionPercent: '0',
	overPercent: '0',
	feePercent: '0',
	feeValue: '0',
	applyMinimum: false,
};

/** 0.06 → "6". La base guarda la fracción con 2 decimales, así que el % es siempre entero. */
export const fractionToPercent = (fraction: number): string => String(Math.round(fraction * 10000) / 100);

/** "6" → 0.06. Solo % enteros: la base no puede guardar 1,5%. */
export const percentToFraction = (percent: string): number => Math.round(Number(percent)) / 100;

export const ruleToForm = (rule: IssueCostCondition): IssueCostRuleForm => ({
	providerId: rule.providerId,
	validatingCarrier: rule.validatingCarrier,
	tariffType: rule.tariffType,
	commissionPercent: fractionToPercent(rule.commission),
	overPercent: fractionToPercent(rule.over),
	feePercent: fractionToPercent(rule.feePercentage),
	feeValue: String(rule.feeValue),
	applyMinimum: rule.applyMinimum,
});

export const formToUpsert = (form: IssueCostRuleForm): IssueCostRuleUpsert => ({
	providerId: form.providerId,
	validatingCarrier: form.validatingCarrier.trim().toUpperCase(),
	tariffType: form.tariffType.trim(),
	commission: percentToFraction(form.commissionPercent),
	over: percentToFraction(form.overPercent),
	feePercentage: percentToFraction(form.feePercent),
	feeValue: Math.round(Number(form.feeValue) * 100) / 100,
	applyMinimum: form.applyMinimum,
});

const isWholePercent = (value: string): boolean => {
	if (value.trim() === '') return false;
	const n = Number(value);
	return Number.isInteger(n) && n >= 0 && n <= 100;
};

// Se valida el texto y no el número: en punto flotante 4.35 * 100 no da 435 exacto.
const AMOUNT = /^\d+(\.\d{1,2})?$/;
const isAmount = (value: string): boolean => AMOUNT.test(value.trim()) && Number(value) <= 100000;

export type IssueCostRuleFormErrors = Partial<Record<keyof IssueCostRuleForm, string>>;

/** Los mismos límites que la API, para avisar antes de mandar. Vacío si está todo bien. */
export const validateRuleForm = (form: IssueCostRuleForm): IssueCostRuleFormErrors => {
	const errors: IssueCostRuleFormErrors = {};
	if (!form.providerId) errors.providerId = 'Elegí un proveedor';
	if (!form.validatingCarrier.trim()) errors.validatingCarrier = 'Código de la aerolínea, ALL o REST';
	else if (form.validatingCarrier.trim().length > 250) errors.validatingCarrier = 'Máximo 250 caracteres';
	if (!form.tariffType.trim()) errors.tariffType = 'Obligatorio (por ejemplo, TODAS)';
	else if (form.tariffType.trim().length > 250) errors.tariffType = 'Máximo 250 caracteres';
	const percentMessage = 'Un % entero entre 0 y 100';
	if (!isWholePercent(form.commissionPercent)) errors.commissionPercent = percentMessage;
	if (!isWholePercent(form.overPercent)) errors.overPercent = percentMessage;
	if (!isWholePercent(form.feePercent)) errors.feePercent = percentMessage;
	if (!isAmount(form.feeValue)) errors.feeValue = 'Un monto entre 0 y 100.000, con hasta 2 decimales';
	return errors;
};

/** La API nueva respondió que la regla no existe (otro la borró): distinto del 404 de una ruta que no existe. */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const isRuleGone = (error: any): boolean => {
	const message: string | undefined = error?.response?.data?.message;
	return error?.response?.status === 404 && Boolean(message) && !message?.startsWith('Not Found');
};

/** El mensaje para el usuario según lo que respondió la API. */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const describeRuleWriteError = (error: any, fallback: string): string => {
	const status: number | undefined = error?.response?.status;
	const apiMessage: string | undefined = error?.response?.data?.message;
	if (status === 403) return 'Solo un administrador puede cambiar las reglas de costo de emisión.';
	if (isRuleGone(error)) return 'La regla ya no existe: alguien la borró. La lista se actualizó.';
	if (status === 404 || status === 405)
		return 'La API de sale orders todavía no tiene esta función: falta deployar la versión nueva.';
	if (status === 409) return 'Ese proveedor ya tiene una regla para esa aerolínea y ese tipo de tarifa.';
	if (status === 423)
		return 'Otro usuario está cambiando las reglas de ese proveedor: probá de nuevo en unos segundos.';
	if (status === 422 && apiMessage) return `La API rechazó los datos: ${apiMessage}`;
	return apiMessage || fallback;
};

/**
 * Cómo usa la calculadora el fee en USD: con "aplica mínimo", el mayor entre el % y el mínimo; sin él, el fijo, salvo
 * que sea 0 (entonces va el %).
 */
export const describeFee = (rule: Pick<IssueCostCondition, 'applyMinimum' | 'feeValue'>): string => {
	if (rule.applyMinimum) return `mínimo USD ${rule.feeValue}`;
	return rule.feeValue === 0 ? 'sin fee fijo (va el %)' : `fee fijo USD ${rule.feeValue}`;
};

/** "LA · TODAS · comisión 6% · over 0% · fee 1% · mínimo USD 5". */
export const describeRule = (rule: IssueCostCondition): string =>
	[
		rule.validatingCarrier,
		rule.tariffType,
		`comisión ${fractionToPercent(rule.commission)}%`,
		`over ${fractionToPercent(rule.over)}%`,
		`fee ${fractionToPercent(rule.feePercentage)}%`,
		describeFee(rule),
	].join(' · ');

/** Las dos reglas guardarían lo mismo. */
export const isSameRule = (a: IssueCostRuleUpsert, b: IssueCostRuleUpsert): boolean =>
	(Object.keys(a) as (keyof IssueCostRuleUpsert)[]).every((key) => a[key] === b[key]);

/** "1 regla", "38 reglas". */
export const rulesCount = (n: number): string => `${n} ${n === 1 ? 'regla' : 'reglas'}`;
